import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import actionsRoutes from '@/router/actions/routes';
import aidesRoutes from '@/router/aides/routes';
import articlesRoutes from '@/router/articles/routes';
import bilanCarboneRoutes from '@/router/bilanCarbone/routes';
import classementRoutes from '@/router/classement/routes';
import coachRoutes from '@/router/coach/routes';
import collectiviteRoutes from '@/router/collectivites/routes';
import compteRoutes from '@/router/compte/routes';
import conformiteRoutes, { RouteConformiteName } from '@/router/conformite/routes';
import kycRoutes from '@/router/kyc/routes';
import personnalisationRoutes from '@/router/personnalisation/routes';
import resetRoutes from '@/router/reset/routes';
import serviceRoutes from '@/router/services/routes';
import thematiqueRoutes from '@/router/thematiques/routes';

const MagicLinkCallBack = () => import('@/components/pages/PageMagicLinkCallback.vue');
const Authentification = () => import('@/components/Authentification.vue');
const FranceConnectLogoutCallBack = () => import('@/components/FranceConnectLogoutCallBack.vue');
const FranceConnectCallBack = () => import('@/components/FranceConnectCallBack.vue');
const PageAccueil = () => import('@/components/pages/PageAccueil.vue');
const PageSessionExpiree = () => import('@/components/pages/PageSessionExpiree.vue');

export enum RouteCommuneName {
  ACCUEIL = 'accueil',
  AUTHENTIFICATION = 'authentification',
  SESSION_EXPIREE = 'session-expiree',
  NOT_FOUND = 'not-found',
  RETOUR_AUTH_FRANCE_CONNECT = 'retour-auth-france-connect',
  RETOUR_LOGOUT_FRANCE_CONNECT = 'retour-logout-france-connect',
  MAGIC_LINK_CALLBACK = 'magic-link-callback',
}

enum RouteCommunePath {
  AUTHENTIFICATION = '/authentification',
  SESSION_EXPIREE = '/session-expiree',
  LOGIN_CALLBACK = '/fc-login-callback',
  LOGOUT_CALLBACK = '/fc-logout-callback',
  MAGIC_LINK_CALLBACK = '/authentification/validation-lien-magique',
}

const routes: RouteRecordRaw[] = [
  ...conformiteRoutes,
  ...kycRoutes,
  ...compteRoutes,
  ...coachRoutes,
  ...articlesRoutes,
  ...aidesRoutes,
  ...thematiqueRoutes,
  ...serviceRoutes,
  ...bilanCarboneRoutes,
  ...classementRoutes,
  ...collectiviteRoutes,
  ...actionsRoutes,
  ...resetRoutes,
  ...personnalisationRoutes,
  {
    path: '/',
    name: RouteCommuneName.ACCUEIL,
    component: PageAccueil,
    meta: {
      title: 'La transition écologique à mon échelle',
      estPublique: true,
    },
  },
  {
    path: RouteCommunePath.AUTHENTIFICATION,
    name: RouteCommuneName.AUTHENTIFICATION,
    component: Authentification,
    meta: {
      title: 'Authentification',
      estPublique: true,
    },
  },
  {
    path: RouteCommunePath.SESSION_EXPIREE,
    name: RouteCommuneName.SESSION_EXPIREE,
    component: PageSessionExpiree,
    meta: {
      title: 'Session expirée',
      estPublique: true,
    },
  },
  {
    path: RouteCommunePath.MAGIC_LINK_CALLBACK,
    name: RouteCommuneName.MAGIC_LINK_CALLBACK,
    component: MagicLinkCallBack,
    meta: {
      estPublique: true,
    },
  },
  {
    path: RouteCommunePath.LOGIN_CALLBACK,
    name: RouteCommuneName.RETOUR_AUTH_FRANCE_CONNECT,
    component: FranceConnectCallBack,
    meta: {
      estPublique: true,
    },
  },
  {
    path: RouteCommunePath.LOGOUT_CALLBACK,
    name: RouteCommuneName.RETOUR_LOGOUT_FRANCE_CONNECT,
    component: FranceConnectLogoutCallBack,
    meta: {
      estPublique: true,
    },
  },
  {
    path: '/:catchAll(.*)',
    name: RouteCommuneName.NOT_FOUND,
    redirect: { name: RouteCommuneName.ACCUEIL },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (to, from, savedPosition) => {
    if (from.path === to.path && to.hash === from.hash) {
      return false;
    }

    // Si la route cible contient une ancre, on défile jusqu'à cet élément avec animation
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      };
    }

    if (savedPosition) {
      return savedPosition;
    } else {
      return { left: 0, top: 0 };
    }
  },
});

const pagesConformiteAutorisees = [
  RouteConformiteName.ACCESSIBILITE,
  RouteConformiteName.MENTIONS_LEGALES,
  RouteConformiteName.CGU,
  RouteConformiteName.POLITIQUE_DE_CONFIDENTIALITE,
];

// Rediriger toutes les URLs vers l'Accueil
router.beforeEach(to => {
  if (to.name !== RouteCommuneName.ACCUEIL && !pagesConformiteAutorisees.includes(to.name as RouteConformiteName)) {
    return { name: RouteCommuneName.ACCUEIL };
  }
});

// Error handling to force refresh the page when a dynamic import fails
router.onError(error => {
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed')
  ) {
    window.location.reload();
  }
});

export default router;
